// src/components/TipoEnvase/TipoEnvaseIndex.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom"; // ✅ Import para regresar
import {
  PageContainer,
  HeaderContainer,
  FormContainer,
  FilterBar,
  Field,
  Label,
  StyledSelect,
  StyledInput,
  AddUserButton,
  GridContainer,
} from "./Users.styles";

import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Toolbar,
  Item,
  Pager,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, {
  SimpleItem,
  Label as DxLabel,
  RequiredRule,
  StringLengthRule,
} from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import { FiEdit, FiTrash2, FiCheckCircle } from "react-icons/fi"; // ✅ Icono de regresar
import notify from "devextreme/ui/notify";
import { apiService } from "../../services/apiService";
import { ApiError } from "../Sucursales/SucursalesForm";
import axios, { AxiosError } from "axios";
import { userService } from "../../services/userService";
import { catalogoService } from "../../services/catalogoService";
import { UserPayload, Users } from "../../dtos/Users";
import {
  isPasswordPolicyValid,
  PASSWORD_POLICY_MESSAGE,
  PASSWORD_UPDATED_MESSAGE,
} from "../../helpers/passwordPolicy";

const NOMINA_MAX_LENGTH = 6;
const NOMINA_MAX_LENGTH_MESSAGE = "Longitud Maxima";

const normalizeRolId = (rol: number | string | undefined): number => {
  if (rol === undefined || rol === null) return 1;
  if (typeof rol === "number") return rol;
  const lower = String(rol).trim().toLowerCase();
  if (lower === "admin" || lower === "administrador" || lower === "0") return 0;
  return 1;
};

const normalizeRolLabel = (rol: number | string | undefined): string =>
  normalizeRolId(rol) === 0 ? "Admin" : "Operador";

const getCurrentUserRole = (): number | null => {
  try {
    const stored = localStorage.getItem("userData");
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    const rol = parsed?.user?.rol ?? parsed?.user?.Rol;
    if (rol === undefined || rol === null) return null;
    return normalizeRolId(rol);
  } catch {
    return null;
  }
};

const isCurrentUserAdmin = (): boolean => getCurrentUserRole() === 0;

const normalizeSearchText = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

interface BackendErrorResponse {
  status?: number;
  data?: string;
  message?: string;
  details?: unknown;
}

/* Helpers */
const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<BackendErrorResponse>;
    const responseData = error.response?.data;
    const errorMessage =
      responseData?.message ||
      (typeof responseData?.data === "string" ? responseData.data : undefined) ||
      "Ocurrió un error en el servidor.";
    return {
      message: errorMessage,
      status: error.response?.status,
      details: responseData?.details ?? null,
    };
  }
  return { message: "Ocurrió un error inesperado. Revisa tu conexión de red." };
};

const UsersList = () => {
  const navigate = useNavigate(); // ✅ Hook para regresar

  const [dataSource, setDataSource] = useState<Users[]>([]);
  const [sucursales, setSucursales] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [editingItem, setEditingItem] = useState<Partial<Users> | null>(null);
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Users | null>(null);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [isPasswordSuccessModalVisible, setIsPasswordSuccessModalVisible] =
    useState(false);
  const [passwordWarning, setPasswordWarning] = useState<string | null>(null);
  const [nominaWarning, setNominaWarning] = useState<string | null>(null);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const formRef = useRef<FormInstance | null>(null);

  const isAdmin = isCurrentUserAdmin();

  // filtros visibles
  const [filterSucursal, setFilterSucursal] = useState<number | "">("");
  const [filterNombre, setFilterNombre] = useState<string>("");

  // filtros aplicados
  const [appliedNombre, setAppliedNombre] = useState<string>("");

  const rolesDisponibles = [
    { id: "Admin", rol: "Admin" },
    { id: "Operador", rol: "Operador" },
  ];

  useEffect(() => {
    if (!isFormPopupVisible || !editingItem || !formRef.current) return;
    formRef.current.option("formData", { ...editingItem });
  }, [isFormPopupVisible, editingItem]);

  /** Pide usuarios al backend, opcionalmente filtrados por sucursal */
  const fetchUsers = async (sucursalId?: number) => {
    setLoading(true);
    try {
      const resp = await userService.getFiltered(sucursalId);
      if (resp.error)
        throw new Error(resp.error.message || "Error al cargar usuarios.");
      setDataSource(resp.data || []);
      if (!resp.data || resp.data.length === 0) {
        notify({
          message: sucursalId
            ? "Sin usuarios para la sucursal seleccionada."
            : "Sin usuarios registrados.",
          type: "info",
          displayTime: 2500,
        });
      }
    } catch (err: any) {
      setDataSource([]);
      notify({ message: err.message, type: "error", displayTime: 3500 });
    } finally {
      setLoading(false);
    }
  };

  // Inicialización
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [branchesResponse] = await Promise.all([
          catalogoService.getBranch(),
        ]);
        if (branchesResponse.error)
          throw new Error(branchesResponse.error.message);
        setSucursales(branchesResponse.data || []);

        const stored = localStorage.getItem("userData");
        const sucursalId = stored
          ? Number(JSON.parse(stored)?.user?.sucursalId)
          : undefined;
        if (sucursalId != null && !Number.isNaN(sucursalId)) {
          setFilterSucursal(sucursalId);
          await fetchUsers(sucursalId);
        } else {
          setLoading(false);
        }
      } catch (err: any) {
        notify({ message: err.message, type: "error", displayTime: 3500 });
        setLoading(false);
      }
    };
    init();
  }, []);

  // Filtro en memoria por nombre
  const visibleUsers = useMemo(() => {
    const name = normalizeSearchText(appliedNombre || "");
    if (!name) return dataSource;
    return (dataSource || []).filter((u) =>
      normalizeSearchText(u.nombre || "").includes(name)
    );
  }, [dataSource, appliedNombre]);

  const handleSearch = async () => {
    const sucursalId =
      filterSucursal === "" || filterSucursal == null
        ? undefined
        : Number(filterSucursal);

    await fetchUsers(sucursalId);
    setAppliedNombre(filterNombre);
  };

  const handleAdd = () => {
    setPasswordWarning(null);
    setNominaWarning(null);
    setIsSaveDisabled(false);
    setEditingItem({ activo: true, rol: "Operador", is365: false });
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: Users) => {
    const itemToEdit = {
      ...data,
      rol: normalizeRolLabel(data.rol),
      password: "",
    };
    setPasswordWarning(null);
    setNominaWarning(null);
    setIsSaveDisabled(false);
    setEditingItem(itemToEdit);
    setIsFormPopupVisible(true);
  };

  const evaluatePasswordField = (password: string | undefined, isEdit: boolean) => {
    if (!password) {
      setPasswordWarning(null);
      setIsSaveDisabled(false);
      return;
    }

    if (isPasswordPolicyValid(password)) {
      setPasswordWarning(null);
      setIsSaveDisabled(false);
      return;
    }

    setPasswordWarning(PASSWORD_POLICY_MESSAGE);
    setIsSaveDisabled(true);
  };

  const handleFormFieldChanged = (e: { dataField?: string; value?: unknown }) => {
    if (e.dataField !== "password") {
      return;
    }

    evaluatePasswordField(
      typeof e.value === "string" ? e.value : undefined,
      !!editingItem?.id
    );
  };

  const handleDelete = (data: Users) => {
    setItemToDelete(data);
    setIsDeletePopupVisible(true);
  };

  const refreshCurrentBranch = async () => {
    const sucursalId =
      filterSucursal === "" || filterSucursal == null
        ? undefined
        : Number(filterSucursal);

    await fetchUsers(sucursalId);
    setAppliedNombre(filterNombre);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await apiService.patch(
        `/api/Usuarios/cambiastatus/${itemToDelete.nomina}`,
        ""
      );
      notify("Cambio de estatus realizado con éxito.", "success", 2000);
      await refreshCurrentBranch();
    } catch {
      notify("Error al cambiar el estatus del registro.", "error", 3000);
    } finally {
      setIsDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleSave = async () => {
    const form = formRef.current;
    if (!form || isSaveDisabled) return;

    const formValidation = await form.validate();
    if (!formValidation.isValid) {
      notify(
        "Por favor, complete todos los campos requeridos.",
        "warning",
        2000
      );
      return;
    }

    const formData = form.option("formData") as Users;
    const isEditing = !!formData.id;
    const passwordProvided = !!formData.password?.trim();

    if (!isEditing) {
      if (!isPasswordPolicyValid(formData.password)) {
        setPasswordWarning(PASSWORD_POLICY_MESSAGE);
        setIsSaveDisabled(true);
        return;
      }
    } else if (passwordProvided) {
      if (!isAdmin) {
        notify(
          "Solo un administrador puede cambiar contraseñas.",
          "error",
          3000
        );
        return;
      }

      if (!isPasswordPolicyValid(formData.password)) {
        setPasswordWarning(PASSWORD_POLICY_MESSAGE);
        setIsSaveDisabled(true);
        return;
      }
    }

    try {
      if (isEditing) {
        const payload: Record<string, unknown> = {
          nombre: formData.nombre,
          nomina: formData.nomina,
          correo: formData.correo,
          rol: normalizeRolId(formData.rol),
          sucursalId: formData.sucursalId,
          activo: formData.activo ?? true,
          is365: formData.is365 ?? false,
        };

        if (passwordProvided) {
          payload.password = formData.password!.trim();
        }

        await apiService.put<Users, Record<string, unknown>>(
          `/api/Usuarios/${formData.id}`,
          payload
        );

        if (passwordProvided) {
          setIsFormPopupVisible(false);
          setIsPasswordSuccessModalVisible(true);
        } else {
          notify("Usuario actualizado con éxito.", "success", 2000);
          setIsFormPopupVisible(false);
        }
      } else {
        const payload: UserPayload = {
          ...formData,
          rol: normalizeRolId(formData.rol),
          password: formData.password!,
          is365: !!formData.is365,
        };
        await apiService.post<Users, Users>("/api/Usuarios", payload);
        notify("Registro creado con éxito.", "success", 2000);
        setIsFormPopupVisible(false);
      }

      await refreshCurrentBranch();
    } catch (error) {
      const apiError = handleApiError(error);
      notify(apiError.message, "error", 3000);
    }
  };

  const EstatusCell = (cellInfo: any) => {
    const isActive = cellInfo.value === true;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: any) => {
    const item = cellData.data as Users;
    const isActive = item.activo;
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button
          hint="Editar"
          onClick={() => handleEdit(item)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button
          hint={isActive ? "Desactivar" : "Activar"}
          onClick={() => handleDelete(item)}
          stylingMode="text"
        >
          {isActive ? (
            <FiTrash2 size={18} color="#dc2626" />
          ) : (
            <FiCheckCircle size={18} color="#16a34a" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <PageContainer>
      {/* ✅ Botón de Regresar */}
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} // ← vuelve a la pestaña anterior
        />
      </div>

      <HeaderContainer>Catálogo de Usuarios</HeaderContainer>

      {/* Panel de Búsqueda */}
      <FilterBar>
        <Field>
          <Label>Sucursal</Label>
          <StyledSelect
            value={filterSucursal}
            onChange={(e) =>
              setFilterSucursal(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
          >
            <option value="">Todas</option>
            {sucursales.map((s) => {
              const id = s.id ?? s.sucursalId ?? s.value ?? "";
              const num = s.numSucursal ?? s.cc ?? s.numero ?? "";
              const name = s.nombreSuc ?? s.nombre ?? s.name ?? "";
              const label =
                [num, name].filter(Boolean).join(" - ") || String(id);
              return (
                <option key={id} value={id}>
                  {label}
                </option>
              );
            })}
          </StyledSelect>
        </Field>

        <Field>
          <Label>Nombre</Label>
          <StyledInput
            placeholder="Ej. Juan Pérez"
            value={filterNombre}
            onChange={(e) => setFilterNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
        </Field>

        <AddUserButton onClick={handleSearch}>Buscar</AddUserButton>
      </FilterBar>

      <GridContainer>
        <DataGrid
          dataSource={visibleUsers}
          keyExpr="id"
          wordWrapEnabled
          showBorders
          columnAutoWidth
          hoverStateEnabled
          loadPanel={{ enabled: loading }}
        >
          <Paging defaultPageSize={10} />
          <Toolbar>
            <Item name="searchPanel" />
            <Item
              location="after"
              widget="dxButton"
              options={{
                text: "Añadir Usuario",
                icon: "add",
                stylingMode: "contained",
                type: "default",
                onClick: handleAdd,
              }}
            />
          </Toolbar>
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel
            visible={true}
            highlightCaseSensitive={true}
            placeholder="Buscar..."
          />
          <Column
            caption="CC - Sucursal"
            calculateCellValue={(row: Users) => {
              const nom = row?.nombreSuc ?? "";
              const num = row?.numSucursal ?? "";
              if (!nom && !num) return "";
              if (!nom) return String(num);
              if (!num) return nom;
              return `${num} - ${nom}`;
            }}
          />
          <Column dataField="nombre" caption="Nombre" />
          <Column dataField="nomina" caption="Nómina" />
          <Column dataField="correo" caption="Correo" />
          <Column dataField="rol" caption="Rol" />
          <Column
            dataField="activo"
            caption="Estatus"
            alignment="center"
            cellRender={EstatusCell}
          />
          <Column
            caption="Acciones"
            alignment="center"
            width={120}
            cellRender={ActionsCell}
          />
          <Paging defaultPageSize={50} />
          <Pager
            visible={false}
            allowedPageSizes={[50]}
            showPageSizeSelector
            showInfo
            showNavigationButtons
          />
        </DataGrid>
      </GridContainer>

      {/* Popup de Edición / Creación */}
      <Popup
        visible={isFormPopupVisible}
        onHiding={() => {
          setIsFormPopupVisible(false);
          setNominaWarning(null);
        }}
        dragEnabled={false}
        showCloseButton
        showTitle
        title={
          editingItem && editingItem.id ? "Editar Usuario" : "Añadir Usuario"
        }
        width={500}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Guardar",
              type: "success",
              stylingMode: "contained",
              disabled: isSaveDisabled,
              onClick: handleSave,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              type: "default",
              stylingMode: "text",
              onClick: () => setIsFormPopupVisible(false),
            },
          },
        ]}
      >
        <FormContainer>
          <Form
            key={editingItem?.id ? String(editingItem.id) : "new-user"}
            formData={editingItem}
            onInitialized={(e) => (formRef.current = e.component ?? null)}
            onFieldDataChanged={handleFormFieldChanged}
          >
            <SimpleItem dataField="nombre">
              <DxLabel text="Nombre" />
              <RequiredRule message="El nombre es requerido." />
            </SimpleItem>
            <SimpleItem
              dataField="nomina"
              editorOptions={{
                maxLength: NOMINA_MAX_LENGTH,
                onKeyDown: (e: {
                  event?: KeyboardEvent;
                  component?: { option: (name: string) => unknown };
                }) => {
                  const event = e.event;
                  if (!event || event.ctrlKey || event.metaKey || event.altKey) {
                    return;
                  }
                  if (event.key.length !== 1) {
                    return;
                  }
                  const current = String(e.component?.option("value") ?? "");
                  if (current.length >= NOMINA_MAX_LENGTH) {
                    event.preventDefault();
                    setNominaWarning(NOMINA_MAX_LENGTH_MESSAGE);
                  }
                },
                onValueChanged: (e: { value?: string }) => {
                  if ((e.value ?? "").length < NOMINA_MAX_LENGTH) {
                    setNominaWarning(null);
                  }
                },
              }}
            >
              <DxLabel text="Nomina" />
              <RequiredRule message="La nomina es requerida." />
              <StringLengthRule
                max={NOMINA_MAX_LENGTH}
                message={NOMINA_MAX_LENGTH_MESSAGE}
              />
            </SimpleItem>
            {nominaWarning && (
              <SimpleItem
                render={() => (
                  <div
                    style={{
                      color: "#b45309",
                      fontSize: "13px",
                      marginTop: "-8px",
                      marginBottom: "8px",
                    }}
                  >
                    {nominaWarning}
                  </div>
                )}
              />
            )}
            <SimpleItem dataField="correo">
              <DxLabel text="Correo" />
              <RequiredRule message="El correo es obligatorio" />
            </SimpleItem>
            <SimpleItem
              dataField="rol"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: rolesDisponibles,
                valueExpr: "id",
                displayExpr: "rol",
                placeholder: "Seleccione",
              }}
            >
              <DxLabel text="Rol" />
              <RequiredRule message="Debes seleccionar un rol." />
            </SimpleItem>
            {!editingItem?.id && (
              <SimpleItem
                dataField="password"
                editorOptions={{ mode: "password", maxLength: 12 }}
              >
                <DxLabel text="Contraseña" />
                <RequiredRule message="La contraseña es requerida." />
              </SimpleItem>
            )}
            {!!editingItem?.id && isAdmin && (
              <SimpleItem
                dataField="password"
                editorOptions={{
                  mode: "password",
                  maxLength: 12,
                  placeholder: "Dejar vacío para no cambiar",
                }}
              >
                <DxLabel text="Cambiar contraseña" />
              </SimpleItem>
            )}
            {passwordWarning && (
              <SimpleItem
                render={() => (
                  <div
                    style={{
                      color: "#b45309",
                      fontSize: "13px",
                      marginTop: "-8px",
                      marginBottom: "8px",
                    }}
                  >
                    {passwordWarning}
                  </div>
                )}
              />
            )}
            <SimpleItem
              dataField="sucursalId"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: sucursales,
                valueExpr: "id",
                displayExpr: (item: { nombreSuc: any; numSucursal: any }) => {
                  if (!item) return "";
                  return `${item.numSucursal} - ${item.nombreSuc}`;
                },
                placeholder: "Seleccione",
              }}
            >
              <DxLabel text="Sucursal" />
              <RequiredRule message="Debes seleccionar una sucursal." />
            </SimpleItem>
          </Form>
        </FormContainer>
      </Popup>

      <Popup
        visible={isPasswordSuccessModalVisible}
        onHiding={() => setIsPasswordSuccessModalVisible(false)}
        dragEnabled={false}
        showCloseButton
        showTitle
        title="Contraseña actualizada"
        width={420}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Aceptar",
              type: "success",
              stylingMode: "contained",
              onClick: () => setIsPasswordSuccessModalVisible(false),
            },
          },
        ]}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>{PASSWORD_UPDATED_MESSAGE}</p>
        </div>
      </Popup>

      {/* Popup de confirmación */}
      <Popup
        visible={isDeletePopupVisible}
        onHiding={() => setIsDeletePopupVisible(false)}
        dragEnabled={false}
        showTitle
        title={`Confirmar ${
          itemToDelete?.activo ? "Desactivación" : "Activación"
        }`}
        width={400}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: `Sí, ${itemToDelete?.activo ? "Desactivar" : "Activar"}`,
              type: itemToDelete?.activo ? "danger" : "success",
              stylingMode: "contained",
              onClick: async () => {
                await confirmDelete();
              },
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              stylingMode: "text",
              onClick: () => setIsDeletePopupVisible(false),
            },
          },
        ]}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>
            ¿Estás seguro de que deseas{" "}
            <strong>{itemToDelete?.activo ? "desactivar" : "activar"}</strong>{" "}
            el usuario
          </p>
          <p style={{ fontWeight: "bold", margin: "10px 0" }}>
            "{itemToDelete?.nombre}"?
          </p>
        </div>
      </Popup>
    </PageContainer>
  );
};

export default UsersList;
